/**
 * Cloud Socket Service
 *
 * This service is responsible for handling Web Socket connections
 * to the corresponding IoTN services.
 * The instances depents on the user's notification urls.
 */

import { handle_events } from './socket_events';
import { SocketInstance } from './socket_instance';
import { crefresh_token } from './http';

//Stores
import { user_data } from 'src/stores/account/user';
import { expireTime } from './auth_token';

let sockets: Array<SocketInstance> = [];

export const get_sockets_length = (): number => sockets.length;

export const get_socket = (host: string): SocketInstance => {
	return sockets.find((sock) => sock.host === host);
};

/**
 * Migration: old notification URLs were using HTTP protocol (as of Legacy Login API).
 * Transform legacy notification URLS them to WebSocket URLs.
 * @deprecated
 * @todo: Remove this function after full migration to IOTU v2 login
 * @param {string} notification_url notification_url - The URL to the notification service.
 * @returns {string} - The WebSocket address for the notification service.
 */
export const legacy_prepare_wss_address = (notification_url: string): string => {
	if (notification_url?.startsWith('https://')) {
		const PORT = 6113;
		const host = new URL(notification_url).hostname;
		return `wss://${host}:${PORT}/shelly/wss/hk_sock`;
	}
	return notification_url;
};

/**
 * @todo: Refactor it to separate auth helper.
 * In addition it should check for token expiration and refresh it if necessary.
 */
const _token_getter = async (): Promise<string> => {
	let token = user_data.getToken();
	if (!token || expireTime(token) <= 0) {
		//@ToDo: Use IOTU v2 login after initial migration
		const refresh_result = await crefresh_token().catch((_) => null);
		token = refresh_result?.token;
		console.log('[SOCKET] Refreshing auth token:', token);
	}
	return token;
};

/**
 * Cloud Socket Connection
 *
 * Handles initial connections and later ensures needed sockets are connected.
 *
 */
export const socket_connect = (): void => {
	console.log('[SOCKET] Connecting cloud socket');
	if (sockets.length) {
		console.warn('[SOCKET] Already connected. Restoring currently opened sockets...');
		socket_restore_connection();
		return;
	}

	const urls = user_data.getNotificationUrls();

	sockets = urls.map((url: string) => {
		const wss = legacy_prepare_wss_address(url);
		return new SocketInstance(wss, handle_events, _token_getter);
	});

	console.log('[SOCKET] Socket instances:', sockets);
};

/**
 * Disconnect all cloud sockets
 */
export const socket_disconnect = (): void => {
	console.warn('[SOCKET] Disconnecting all cloud sockets');
	if (sockets.length > 0) {
		sockets.forEach((instance, i) => {
			instance.close();
		});

		sockets = [];
	}
};

/**
 * Clean reconnect of all cloud sockets
 */
export const socket_reconnect = (): void => {
	console.warn('[SOCKET] Reconnect all cloud sockets');
	socket_disconnect();
	socket_connect();
};

/**
 * Fallback if the socket connection has been lost.
 *
 * Checks if all socket instances are connected or reconnect any of them in case of lost connection.
 * Returns TRUE if there was missing connection.
 * */
export const socket_restore_connection = (): boolean => {
	let restored: boolean = false;
	sockets.forEach((instance, i) => {
		if (instance.getReadyState() !== WebSocket.OPEN) {
			console.warn('[SOCKET] Restoring socket %d:', i, instance.host);
			restored = true;
			instance.reconnect();
		}
	});

	console.log('[SOCKET] Cloud sockets: %d connected. Restore connection: ', sockets.length, restored);
	return restored;
};

/**
 * Check if all cloud socket instances are connected.
 * Returns false if any instance has lost connection
 */
export const socket_is_connected = (): boolean => {
	let isConnected: boolean = sockets.length > 0;
	sockets.forEach((sock, i) => {
		if (sock.getReadyState() !== WebSocket.OPEN) {
			console.warn('[SOCKET] Socket %d is not connected:', i, sock.host);
			isConnected = false;
		}
	});

	console.log('[SOCKET] Cloud sockets: %d available. Connected: ', sockets.length, isConnected);
	return isConnected;
};

/**
 * Handle new WS connection from other user instance.
 * * This is used when received shared device from other users to establish connection to another WS instance.
 * @param {string} notifications_url
 */
export const socket_on_new_connection = (notifications_url: string) => {
	const old_notifications_urls = user_data.getNotificationUrls();
	const notif_data = user_data.getNotificationsData();

	const new_wss_url = `${notif_data.protocol}//${notifications_url}:${notif_data.port}${notif_data.pathname}`;

	if (!old_notifications_urls.includes(new_wss_url)) {
		user_data.update({
			notifications_urls: [...old_notifications_urls, new_wss_url],
		});
		console.log('[SOCKET] New notification URL added:', new_wss_url);
		new SocketInstance(new_wss_url, handle_events, _token_getter);
	}
};
